import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useAsyncData } from "@/hooks/useAsyncData";
import { getListingBySlug } from "@/data/repository";
import { ErrorState } from "@/components/shared/DataState";
import { BRAND } from "@/lib/brand";
import { formatPrice } from "@/lib/utils";
import {
  Bed,
  Bath,
  Maximize,
  MapPin,
  MessageCircle,
  ArrowLeft,
  Building2,
  Play,
  ExternalLink,
  FileText,
  X,
} from "lucide-react";

const isPdf = (u = "") => u.toLowerCase().includes(".pdf");

const STATUS_STYLES = {
  Available: "bg-bronze text-cream",
  Sold: "bg-olive text-cream",
  Rented: "bg-ink text-cream",
  "Coming Soon": "bg-taupe text-cream",
};

function DetailSkeleton() {
  return (
    <div className="container-page py-16 animate-pulse" data-testid="loading-state">
      <div className="h-4 w-32 bg-bronze/10 mb-8" />
      <div className="aspect-[16/9] bg-bronze/10" />
      <div className="mt-8 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          <div className="h-8 w-2/3 bg-bronze/15" />
          <div className="h-4 w-full bg-bronze/10" />
          <div className="h-4 w-5/6 bg-bronze/10" />
        </div>
        <div className="h-48 bg-bronze/10" />
      </div>
    </div>
  );
}

export default function ListingDetailPage() {
  const { slug } = useParams();
  const { data: listing, loading, error, reload } = useAsyncData(
    () => getListingBySlug(slug),
    [slug],
    null
  );
  const [active, setActive] = useState(0);
  const [fpOpen, setFpOpen] = useState(false);

  const images = useMemo(() => {
    if (!listing) return [];
    return Array.from(new Set([listing.image, ...(listing.gallery || [])].filter(Boolean)));
  }, [listing]);

  if (loading) return <DetailSkeleton />;
  if (error)
    return (
      <div className="container-page py-16">
        <ErrorState onRetry={reload} />
      </div>
    );

  if (!listing) {
    return (
      <div className="container-page py-24 text-center" data-testid="listing-not-found">
        <Building2 className="mx-auto text-bronze" size={36} />
        <h1 className="font-serif text-4xl text-ink mt-5">Listing not found</h1>
        <p className="text-ink_soft/70 mt-3">
          This property may have been sold or taken off the market.
        </p>
        <Link to="/listings" className="btn-primary mt-7 inline-block">
          Back to Listings
        </Link>
      </div>
    );
  }

  const status = listing.listingStatus || listing.status;
  const waLink = `https://wa.me/6581005654?text=${encodeURIComponent(
    listing.waMessage || `Hi Kiap, I'm interested in ${listing.title}.`
  )}`;

  return (
    <div data-testid="page-listing-detail" className="bg-cream">
      <div className="container-page py-10 md:py-16">
        <Link
          to="/listings"
          data-testid="back-to-listings"
          className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-bronze hover:text-bronze_dark"
        >
          <ArrowLeft size={14} /> All Listings
        </Link>

        {/* Gallery */}
        <div className="mt-7 relative overflow-hidden bg-beige flex items-center justify-center min-h-[16rem]">
          {images[active] ? (
            <img
              src={images[active]}
              alt={`${listing.title} — view ${active + 1}`}
              data-testid="listing-gallery-main"
              // Natural aspect ratio (capped height) — the full image shows, no crop.
              className="w-auto max-w-full max-h-[72vh] object-contain"
            />
          ) : (
            <div data-testid="listing-gallery-main" className="aspect-[16/9] w-full flex items-center justify-center text-bronze/40 text-[0.7rem] tracking-[0.22em] uppercase">
              No image
            </div>
          )}
          <span
            className={`absolute top-5 left-5 px-3 py-1 text-[0.65rem] tracking-[0.22em] uppercase font-medium ${
              STATUS_STYLES[status] || "bg-bronze text-cream"
            }`}
          >
            {status}
          </span>
        </div>

        {images.length > 1 && (
          <div className="mt-3 flex gap-3 flex-wrap" data-testid="listing-gallery-thumbs">
            {images.map((src, i) => (
              <button
                key={src}
                onClick={() => setActive(i)}
                data-testid={`listing-thumb-${i}`}
                className={`relative h-20 w-28 overflow-hidden border transition-colors ${
                  i === active ? "border-bronze" : "border-bronze/20 hover:border-bronze/60"
                }`}
              >
                <img src={src} alt={`thumbnail ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="mt-10 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="text-[0.7rem] tracking-[0.22em] uppercase text-bronze">
              {listing.type}
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-ink leading-tight mt-2">
              {listing.title}
            </h1>
            <div className="mt-3 flex items-center gap-2 text-ink_soft/70">
              <MapPin size={16} className="text-bronze" />
              <span>{listing.location}</span>
            </div>

            <div className="my-8 shimmer-line" />

            {/<[a-z][\s\S]*>/i.test(listing.description || "") ? (
              <div
                data-testid="listing-description"
                className="prose prose-sm max-w-none text-ink_soft/85 [&_h2]:font-serif [&_h2]:text-ink [&_h3]:font-serif [&_h3]:text-ink [&_a]:text-bronze [&_img]:my-4"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(listing.description || "") }}
              />
            ) : (
              <p data-testid="listing-description" className="text-base font-light leading-relaxed text-ink_soft/85 whitespace-pre-line">
                {listing.description}
              </p>
            )}

            {listing.allowableTrades && (
              <div className="mt-10" data-testid="listing-allowable-trades">
                <div className="text-[0.7rem] tracking-[0.22em] uppercase text-bronze mb-2">Allowable trades</div>
                <p className="text-base font-light leading-relaxed text-ink_soft/85 whitespace-pre-line">
                  {listing.allowableTrades}
                </p>
              </div>
            )}
          </div>

          {/* Sticky detail card */}
          <aside className="lg:sticky lg:top-28 self-start border border-bronze/20 bg-beige/50 p-8">
            <div className="font-serif text-3xl text-ink">{formatPrice(listing)}</div>
            <div className="mt-6 grid grid-cols-2 gap-4 text-sm text-ink_soft/80">
              <div className="flex items-center gap-2">
                <Bed size={16} className="text-bronze" /> {listing.bedrooms} Bedrooms
              </div>
              <div className="flex items-center gap-2">
                <Bath size={16} className="text-bronze" /> {listing.bathrooms} Bathrooms
              </div>
              <div className="flex items-center gap-2">
                <Maximize size={16} className="text-bronze" /> {listing.area}
              </div>
              <div className="flex items-center gap-2">
                <Building2 size={16} className="text-bronze" /> {listing.type}
              </div>
            </div>
            <div className="mt-5 text-xs tracking-widest uppercase text-olive">
              {listing.tenure}
            </div>

            {listing.commercialType && (
              <div className="mt-4 text-sm text-ink_soft/80" data-testid="listing-commercial-type">
                <span className="text-olive">Type:</span> {listing.commercialType}
              </div>
            )}

            {listing.floorplan && (
              <button
                type="button"
                onClick={() => setFpOpen(true)}
                data-testid="listing-floorplan-open"
                className="mt-6 w-full group relative block border border-bronze/30 bg-cream overflow-hidden hover:border-bronze transition-colors"
              >
                {isPdf(listing.floorplan) ? (
                  <div className="flex items-center gap-3 p-4 text-left">
                    <FileText className="text-bronze shrink-0" size={22} />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-ink">Floorplan</div>
                      <div className="text-[11px] text-olive">PDF · click to expand</div>
                    </div>
                    <Maximize size={16} className="text-bronze" />
                  </div>
                ) : (
                  <>
                    <img src={listing.floorplan} alt="Floorplan" className="w-full h-40 object-cover" />
                    <span className="absolute top-2 left-2 bg-cream/90 text-bronze text-[0.6rem] tracking-[0.2em] uppercase px-2 py-0.5">Floorplan</span>
                    <span className="absolute inset-0 flex items-center justify-center bg-ink/0 group-hover:bg-ink/25 transition-colors">
                      <span className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-1.5 bg-cream/90 text-ink text-[0.6rem] tracking-[0.2em] uppercase px-3 py-1.5 transition-opacity">
                        <Maximize size={12} /> Expand
                      </span>
                    </span>
                  </>
                )}
              </button>
            )}
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="listing-detail-enquire"
              className="btn-primary mt-8 w-full inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} /> Enquire on WhatsApp
            </a>
            {listing.videoLink && listing.videoLink !== "#" && (
              <a
                href={listing.videoLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="listing-detail-video"
                className="btn-secondary mt-3 w-full inline-flex items-center justify-center gap-2"
              >
                <Play size={16} /> View Home Tour
              </a>
            )}
            {listing.externalLink && listing.externalLink !== "#" && (
              <a
                href={listing.externalLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="listing-detail-portal"
                className="btn-secondary mt-3 w-full inline-flex items-center justify-center gap-2"
              >
                View on Portal <ExternalLink size={14} />
              </a>
            )}
            <a
              href={BRAND.valuationLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center mt-4 text-[0.7rem] tracking-[0.18em] uppercase text-bronze hover:text-bronze_dark"
            >
              Get a Property Valuation →
            </a>
          </aside>
        </div>
      </div>

      {fpOpen && listing.floorplan && (
        <div
          className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setFpOpen(false)}
          data-testid="listing-floorplan-modal"
        >
          <div className="relative w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-cream text-[0.7rem] tracking-[0.22em] uppercase">Floorplan — {listing.title}</span>
              <div className="flex items-center gap-4">
                <a href={listing.floorplan} target="_blank" rel="noopener noreferrer" className="text-cream/80 hover:text-cream text-xs inline-flex items-center gap-1.5">
                  <ExternalLink size={14} /> Open
                </a>
                <button onClick={() => setFpOpen(false)} className="text-cream/80 hover:text-cream" aria-label="Close" data-testid="listing-floorplan-close">
                  <X size={20} />
                </button>
              </div>
            </div>
            {isPdf(listing.floorplan) ? (
              <iframe title="Floorplan" src={listing.floorplan} className="w-full h-[82vh] bg-cream" />
            ) : (
              <img src={listing.floorplan} alt="Floorplan" className="w-full max-h-[85vh] object-contain bg-cream" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
